import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: string;
  image?: string;
  structuredData?: object | object[];
  noindex?: boolean;
}

const BASE_URL = "https://bookkeepingservices.co.za";

/**
 * Per-page head tags.
 *
 * Rules enforced here:
 *  - Exactly one <title>, <meta name="description">, <link rel="canonical">.
 *  - Self-referencing canonical built from `canonical` (path only, no domain).
 *  - No hreflang: this is a single-market (en-ZA) site. Adding en-GB/US/AU
 *    variants that resolve to the same URL creates "more than one URL for the
 *    same hreflang" errors in Ahrefs and Search Console.
 *  - og:image is set only when the caller passes one; otherwise Lovable's
 *    hosting layer injects the site social preview at serve time. We do NOT
 *    ship a hardcoded /og-image.jpg that returns 404.
 *  - noindex flips both robots and googlebot for utility / private pages.
 */
export const SEO = ({
  title,
  description,
  canonical,
  type = "website",
  image,
  structuredData,
  noindex,
}: SEOProps) => {
  // Normalise canonical: always absolute, no trailing slash except root.
  let path = canonical ?? "/";
  if (!path.startsWith("/")) path = `/${path}`;
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  const fullCanonical = `${BASE_URL}${path}`;

  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${BASE_URL}${image}`
    : undefined;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullCanonical} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:alt" content={title} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* Structured Data */}
      {structuredData &&
        (Array.isArray(structuredData) ? (
          structuredData.map((data, i) => (
            <script key={i} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        ))}
    </Helmet>
  );
};
